import { useRequest } from '@/api/hooks';
import { useBaseInfo } from '@/context/BaseInfoContext';
import { DownOutlined, HomeOutlined, ShopOutlined } from '@ant-design/icons';
import { Button, Modal, Tag } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { usePerspectiveStyles } from './style';
import Loading from '@/components/Loading';

interface ViewScopeInfo {
  tenantId: number;
  tenantCode: string;
  poiId: number;
  poiName: string;
  type: 'HQ' | 'POI';
  roles: number[];
  staffId: number;
}

interface TenantOrgsNode {
  tenantId: number;
  tenantName: string;
  tenantCode: string;
  orgs: ViewScopeInfo[];
}

export const PerspectiveSwitcher = () => {
  const [baseInfo] = useBaseInfo();
  const { styles, cx } = usePerspectiveStyles();
  const [modalOpen, setModalOpen] = useState(false);

  // Fetch all available orgs (auto-fetches on mount)
  const {
    send: fetchOrgData,
    payload: orgData,
    loading: orgLoading,
  } = useRequest<TenantOrgsNode[]>(
    {
      method: 'get',
      url: '/auth/availableOrgs',
    },
    { manual: true }
  );

  // Switch org mutation
  const { send: switchOrg, loading: switching } = useRequest<any>(
    {
      method: 'post',
      url: '/auth/switchOrg',
    },
    { manual: true }
  );

  const currentViewName = useMemo(() => {
    return !baseInfo.poi ? baseInfo?.tenantName || '' : baseInfo?.poiName || '';
  }, [baseInfo]);

  const handleSwitch = async (tenantId: number, poiId: number) => {
    if (switching) return;
    try {
      await switchOrg({ data: { tenantId, poiId } });
      setModalOpen(false);
      window.location.reload();
    } catch {
      // Error handled by api interceptor
    }
  };

  useEffect(() => {
    if (modalOpen && !orgData && !orgLoading) {
      fetchOrgData();
    }
  }, [modalOpen, orgData, orgLoading]);

  // Only one accessible org: no arrow, not clickable
  if (!baseInfo.scopeNum || baseInfo.scopeNum <= 1) {
    return <span>{currentViewName}</span>;
  }

  // Multiple orgs: clickable trigger + modal popup
  return (
    <>
      <Button
        onClick={() => setModalOpen(true)}
        style={{
          backgroundColor: 'transparent',
          color: '#fff',
          borderColor: 'transparent',
        }}>
        {currentViewName}
        <DownOutlined style={{ fontSize: 10 }} />
      </Button>

      <Modal
        title="切换视角"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={380}
        destroyOnHidden>
        <div className={styles.modalBody}>
          {orgLoading && <Loading className={'mb-s mt-s'} />}
          {(orgData || []).map((tenant, tIndex) => (
            <div key={tenant.tenantId}>
              {tIndex > 0 && <div className={styles.tenantDivider} />}
              <div className={styles.tenantLabel}>{tenant.tenantName}</div>
              {tenant.orgs.map(org => {
                const current = org.tenantCode === baseInfo.tenantCode && org.poiId === baseInfo.poiId;
                return (
                  <div
                    key={`${org.tenantId}-${org.poiId}`}
                    className={cx(styles.orgItem, { active: current })}
                    onClick={() => !current && handleSwitch(org.tenantId, org.poiId)}>
                    <span className={styles.orgIcon}>{org.type === 'HQ' ? <HomeOutlined /> : <ShopOutlined />}</span>
                    <span className={styles.orgName}>{org.poiName}</span>
                    {current && <span className={styles.currentMark}>✓</span>}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </Modal>
    </>
  );
};
